import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { User, ShoppingBag, Heart, MapPin, Mail, Phone } from "lucide-react";

const EMAIL_KEY = "ttfm_customer_email";

function getStoredEmail() {
  try { return localStorage.getItem(EMAIL_KEY) || localStorage.getItem("ttfm_wishlist_email") || ""; } catch { return ""; }
}

export default function Account() {
  const [params, setParams] = useSearchParams();
  const urlEmail = params.get("email") || getStoredEmail();
  const [email, setEmail] = useState(urlEmail);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [temp, setTemp] = useState(urlEmail || "");

  useEffect(() => {
    const fromUrl = params.get("email");
    if (fromUrl) {
      try { localStorage.setItem(EMAIL_KEY, fromUrl.toLowerCase()); } catch { /* ignore */ }
      setEmail(fromUrl.toLowerCase());
    }
  }, [params]);

  useEffect(() => {
    if (!email) return;
    setLoading(true);
    api.get(`/profile/by-email?email=${encodeURIComponent(email)}`)
      .then((r) => setProfile(r.data))
      .catch(() => toast.error("Could not load your profile."))
      .finally(() => setLoading(false));
  }, [email]);

  function submitEmail(e) {
    e.preventDefault();
    const v = (temp || "").trim().toLowerCase();
    if (!v || !/.+@.+\..+/.test(v)) { toast.error("Please enter a valid email."); return; }
    try { localStorage.setItem(EMAIL_KEY, v); } catch { /* ignore */ }
    setEmail(v);
    setParams({ email: v }, { replace: true });
  }

  function signOut() {
    try { localStorage.removeItem(EMAIL_KEY); } catch { /* ignore */ }
    setEmail(""); setProfile(null); setTemp("");
    setParams({}, { replace: true });
  }

  // No email yet → gentle prompt
  if (!email) {
    return (
      <div className="max-w-md mx-auto px-6 py-24" data-testid="account-no-email">
        <div className="text-center">
          <User className="w-10 h-10 text-[var(--accent)] mx-auto" />
          <h1 className="font-serif text-3xl mt-4">Your account</h1>
          <p className="text-sm text-[#5C5547] mt-2">No login needed. Enter your email — we'll bring up your orders, wishlist, and saved addresses.</p>
        </div>
        <form onSubmit={submitEmail} className="mt-6 space-y-3">
          <input type="email" value={temp} onChange={(e) => setTemp(e.target.value)} required autoFocus
            placeholder="you@email.com"
            data-testid="account-email-input"
            className="w-full px-4 py-3 rounded-xl bg-[#FDFBF7] border border-[#EAE4D3] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm" />
          <button type="submit" data-testid="account-email-submit"
            className="w-full py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] hover:bg-[#1A1815] text-sm">
            Open my account
          </button>
        </form>
      </div>
    );
  }

  const addr = profile?.default_address;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="account-page">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-10">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Welcome back{profile?.name ? `, ${profile.name.split(" ")[0]}` : ""}.</h1>
          <p className="text-sm text-[#5C5547] mt-1">Signed in as <span className="text-[#2C2924]">{email}</span></p>
        </div>
        <button onClick={signOut} data-testid="account-signout" className="text-xs text-[#5C5547] hover:text-[var(--accent)] underline">Use a different email</button>
      </header>

      {loading && <div className="text-sm text-[#5C5547]">Loading…</div>}

      {!loading && profile && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Saved info card */}
          <section className="rounded-3xl bg-[#FDFBF7] border border-[#EAE4D3] p-6" data-testid="account-info-card">
            <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547] mb-4">Your info</div>
            <div className="space-y-2 text-sm">
              {profile.name && <div className="flex items-center gap-2"><User className="w-4 h-4 text-[var(--accent)]" /> {profile.name}</div>}
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[var(--accent)]" /> {profile.email}</div>
              {profile.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[var(--accent)]" /> {profile.phone}</div>}
              {addr ? (
                <div className="flex items-start gap-2 pt-2 border-t border-[#EAE4D3] mt-3">
                  <MapPin className="w-4 h-4 text-[var(--accent)] mt-0.5 shrink-0" />
                  <div className="text-[#5C5547]">
                    {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}<br />
                    {addr.city}, {addr.state} {addr.postal_code}<br />
                    {addr.country}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-[#5C5547] pt-2 border-t border-[#EAE4D3] mt-3">No saved address yet. It'll auto-save from your first order.</div>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Link to="/wishlist" className="px-3 py-2 rounded-full bg-[#F5F2EA] text-center text-xs flex items-center justify-center gap-1.5">
                <Heart className="w-3.5 h-3.5" /> Wishlist ({profile.wishlist_count})
              </Link>
              <Link to="/orders" className="px-3 py-2 rounded-full bg-[#F5F2EA] text-center text-xs flex items-center justify-center gap-1.5">
                <ShoppingBag className="w-3.5 h-3.5" /> Orders ({profile.order_count})
              </Link>
            </div>
          </section>

          {/* Recent orders */}
          <section className="lg:col-span-2 rounded-3xl bg-[#FDFBF7] border border-[#EAE4D3] p-6" data-testid="account-orders">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-serif text-2xl">Your orders</h2>
              {profile.order_count > 0 && <span className="text-xs text-[#5C5547]">{profile.order_count} total</span>}
            </div>
            {profile.orders.length === 0 && (
              <div className="text-sm text-[#5C5547]">No orders yet — <Link to="/shop" className="underline text-[var(--accent)]">browse the shop</Link>.</div>
            )}
            <div className="space-y-2">
              {profile.orders.slice(0, 10).map((o) => (
                <div key={o.id} data-testid={`account-order-${o.id}`} className="flex items-start justify-between gap-3 p-3 rounded-2xl border border-[#EAE4D3]">
                  <div className="min-w-0">
                    <div className="font-medium text-sm line-clamp-1">{o.product_title}{o.variant_name ? ` — ${o.variant_name}` : ""}</div>
                    <div className="text-[11px] text-[#5C5547] mt-0.5">
                      {new Date(o.created_at).toLocaleDateString()} · {o.payment_method}
                      {o.printful_order_id && ` · printful #${o.printful_order_id}`}
                    </div>
                  </div>
                  <div className={`text-[10px] uppercase tracking-[0.15em] ${
                    o.status === "fulfilled" ? "text-green-700" : o.status === "paid" ? "text-[var(--accent)]" : "text-[#5C5547]"
                  }`}>
                    {o.status}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
